import React, { useState } from "react";

import classNames from "classnames";
import { motion, AnimatePresence } from "framer-motion";
import isEmpty from "lodash.isempty";
import { observer } from "mobx-react";
import Image from "next/legacy/image";
import Link from "next/link";
import { usePathname } from "../../shared/hooks/useNavigation";
import PropTypes from "prop-types";
import { Nav } from "react-bootstrap";
import styled from "styled-components";

import ComponentContext from "../../contexts/ComponentContext";
import { useStores } from "../../contexts/StoreContext";
import BurgerMenuIcon from "../../public/burgerMenu.svg";
import CrossIcon from "../../public/cross.svg";
import JobAlert from "../../shared/components/JobAlert/JobAlert";
import useDevice from "../../shared/customHooks/useDevice";
import Markdown from "../../shared/styles/Markdown";
import SocialMediaLinks from "../Footer/SocialMediaLinks";
import StyledLink from "./StyledLink";

const StyledNavbarContainer = styled.div`
  ${(props) => props.dynamicStyles || ""}
`;

const StyledMenuItem = styled(Nav.Item)`
  font-size: 2rem;
  text-align: center;
  width: fit-content;
  margin: 0 auto;
`;

const StyledNavbar = styled.div`
  background: ${({ theme }) => theme.primaryColor || "#FF5722"}; // Orange color
  width: ${({ logoWidth }) => logoWidth || "50"}px;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: fixed;
  top: 0;
  left: 0;
  height: 100vh;
  z-index: 13;
`;

const StyledMenuButton = styled.button`
  background: none;
  border: none;
  color: white;
  font-size: 24px;
  margin-top: 1rem;
  cursor: pointer;
  z-index: 12;
`;

const StyledLogo = styled.div`
  width: ${({ logoWidth }) => logoWidth || "50"}px;
  height: ${({ logoHeight }) => logoHeight || "50"}px;
  margin: 20px 0;
  position: relative;
`;

const StyledText = styled.div`
  display: flex;
  position: relative;
  flex-grow: 1;
  width: 100%;
  height: calc(100% - 140px);
  color: ${({ color }) => color || "white"};

  h2 {
    font: bold 12px Sans-Serif;
    letter-spacing: 2px;
    text-transform: uppercase;
    color: #fff;
    padding: 5px 10px;
    margin: 0;
    line-height: 24px;
    position: absolute;
    top: 50%;
    left: 50%;
    width: 100vh; // Use viewport height for width
    transform-origin: 0 0;
    transform: rotate(-90deg) translate(-50%, -50%);
    text-align: center;
    white-space: nowrap;
  }
`;

const StyledExpandedMenu = styled.div`
  width: 100%;
  padding: 3rem 4rem;
  color: black; /* Adjust text color */
  background: #f5f5f5; /* Adjust background color */
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const StyledInnerMenu = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 1rem;
  flex-grow: 1;
`;

const StyledMenu = styled(motion.div)`
  background: #f5f5f5;
  width: ${({ isMobile, logoWidth }) =>
    isMobile ? `calc(100% - ${logoWidth}px)` : "fit-content"};
  top: 0;
  left: ${({ logoWidth }) => logoWidth || "50"}px;

  max-width: ${({ logoWidth }) => `calc(100vw - ${logoWidth}px)`};
  height: 100vh;
  overflow-x: hidden;
  position: fixed;
  z-index: 12;
`;

function SideNavigation(props) {
  const { settings, customLogo } = props;
  const { configStore, navigationStore } = useStores();
  const { config } = configStore;
  const { isMobile } = useDevice();
  const { topNavigation } = navigationStore;
  const currentPath = usePathname();

  const [isOpen, setIsOpen] = useState(false);
  const showSocialMediaNavigation = !isEmpty(settings?.socialMediaNavigation);
  let logoUrl = config?.logo?.url;
  let logoHeight = config?.logoHeight;
  let logoWidth = config?.logoWidth;

  const socialMediaLinksClassName = classNames(
    "mx-auto d-none d-lg-block",
    settings?.socialMediaNavigation?.socialMediaWrapperClassName
  );

  if (customLogo?.url) {
    logoUrl = customLogo?.url;
    logoHeight = customLogo?.height;
    logoWidth = customLogo?.width;
  }

  const menuVariants = {
    open: {
      x: 0,
      transition: {
        type: "spring",
        damping: 20,
      },
    },
    closed: {
      x: "-100%",
      transition: {
        type: "spring",
        damping: 20,
      },
    },
  };

  return (
    <StyledNavbarContainer dynamicStyles={settings?.CSS} id="side-navigation">
      <StyledNavbar logoWidth={logoWidth} id="navbar">
        <StyledMenuButton
          className="menu-button"
          onClick={() => setIsOpen(!isOpen)}
        >
          {isOpen ? <CrossIcon /> : <BurgerMenuIcon />}
        </StyledMenuButton>
        <StyledText color={settings?.navbarText} className="menu-text">
          <h2>{settings?.additionalConfig?.headerTitle}</h2>
        </StyledText>
        {logoUrl && (
          <StyledLogo
            logoHeight={logoHeight}
            logoWidth={logoWidth}
            className="header-logo"
          >
            <Link href={config.logoUrl || "/"}>
              <Image
                alt={config.siteName}
                layout="fill"
                objectFit="contain"
                priority
                src={logoUrl}
              />
            </Link>
          </StyledLogo>
        )}
      </StyledNavbar>
      <AnimatePresence>
        {isOpen && (
          <StyledMenu
            initial="closed"
            animate="open"
            exit="closed"
            variants={menuVariants}
            isMobile={isMobile}
            logoWidth={logoWidth || "50"}
            className="outer-opened-menu"
          >
            <StyledExpandedMenu>
              <StyledInnerMenu>
                <StyledMenuItem id="menu-items" isMobile>
                  {topNavigation.map((child, index) => {
                    const href = child.path
                      ? `/${child.path}`
                      : child.externalPath;
                    return (
                      <StyledLink
                        key={child.id}
                        active={currentPath.includes(child.path)}
                        eventKey={`${child.path}-${index}`}
                        href={href}
                        page={child}
                      >
                        {child.title}
                      </StyledLink>
                    );
                  })}
                </StyledMenuItem>
                {showSocialMediaNavigation && (
                  <SocialMediaLinks
                    iconClassName="me-4 justify-content-center"
                    iconColor={settings?.socialMediaNavigation?.iconColor}
                    iconSize={settings?.socialMediaNavigation?.iconSize}
                    mediaLinksWrapperClassName={socialMediaLinksClassName}
                  />
                )}
                {settings?.jobAlert ? (
                  <ComponentContext.Provider value={settings?.jobAlert}>
                    <JobAlert />
                  </ComponentContext.Provider>
                ) : null}
              </StyledInnerMenu>
              <Markdown>{settings?.navbarText}</Markdown>
            </StyledExpandedMenu>
          </StyledMenu>
        )}
      </AnimatePresence>
    </StyledNavbarContainer>
  );
}

SideNavigation.defaultProps = {
  customLogo: null,
  settings: null,
};

SideNavigation.propTypes = {
  customLogo: PropTypes.shape({}),
  settings: PropTypes.shape({
    CSS: PropTypes.string,
    background: PropTypes.string,
    customDesktopNavbar: PropTypes.string,
    navbarText: PropTypes.string,
    showLocaleSelector: PropTypes.bool,
    showSearch: PropTypes.bool,
  }),
};

export default observer(SideNavigation);
